import queryString from "query-string";
import { type ApiResponse } from "shared/api/ApiResponse";
import { type BaseApiContract } from "shared/api/BaseApiContract";
import { HttpMethod } from "shared/api/http";
import { pillarApiErrorFromResponse } from "shared/api/PillarApiError";
import {
  type PaginationRequest,
  type PaginatedResults,
} from "shared/api/types/pagination";
import { type FiltersRequest } from "shared/filter-where-clause";

export const RequestHeader = {
  defaultJSON: { "Content-Type": "application/json" },
};

export const addFiltersAndPaginationToUrl = (
  url: string,
  filters: FiltersRequest,
  paginationRequest?: PaginationRequest,
) => {
  const stringified = queryString.stringifyUrl(
    {
      url: url,
      query: { ...filters, ...paginationRequest },
    },
    {
      skipNull: true,
      skipEmptyString: true,
      arrayFormat: "bracket",
    },
  );
  return stringified;
};

type ReadErrorBodyProps = {
  /** The failed response; read through a clone so it stays unread */
  response: Response;
};

/**
 * Reads a failed response as the JSON body `handleError` writes.
 *
 * A body that is not JSON at all - a proxy timeout page, an empty response -
 * reads as no body rather than throwing a parse error over the real failure.
 * The read happens on a clone so the untouched response is still there for
 * anyone debugging in the network tab.
 */
const readErrorBody = async ({
  response,
}: ReadErrorBodyProps): Promise<ApiResponse<unknown> | undefined> => {
  try {
    return (await response.clone().json()) as ApiResponse<unknown>;
  } catch {
    return undefined;
  }
};

interface Params {
  [key: string]: number | string | (number | string)[];
}
export type ExtractContractInputs<T> = T extends {
  paramsType?: infer P;
  bodyType?: infer B;
}
  ? { params?: P; body?: B }
  : never;

const MAX_PARALLEL_REQUESTS = 5;
const TARGET_PAYLOAD_BYTES = 500_000; // ~500KB target per request
const MIN_PAGE_SIZE = 25;
const MAX_PAGE_SIZE = 500;

type ContractRequestProps<T extends BaseApiContract> = {
  /** The contract naming the route, HTTP method and request/response types */
  contract: T;
  /** Route params (unused ones go on the query string) and the request body */
  inputs?: ExtractContractInputs<T>;
  /** Filters added to the query string */
  filters?: FiltersRequest;
  /** Page and page size added to the query string */
  pagination?: PaginationRequest;
  /** Headers sent instead of the JSON content type, for non-GET requests */
  customHeaders?: HeadersInit;
};

export type ApiRequestRawProps<T extends BaseApiContract> =
  ContractRequestProps<T>;

export type ApiRequestProps<T extends BaseApiContract> =
  ContractRequestProps<T> & {
    /** Fetch every page in parallel batches and return them as one page */
    flattenPagination?: boolean;
  };

export type ApiRequestCustomProps<
  T extends BaseApiContract,
  ParsedType,
> = ContractRequestProps<T> & {
  /** Reads the successful response, e.g. to download a file it redirects to */
  parser: (response: Response) => ParsedType | Promise<ParsedType>;
};

const estimatePageSize = (
  results: unknown[],
  currentPageSize: number,
): number => {
  if (results.length === 0) {
    return currentPageSize;
  }
  const payloadBytes = JSON.stringify(results).length;
  const bytesPerItem = payloadBytes / results.length;
  if (bytesPerItem === 0) {
    return currentPageSize;
  }
  const estimated = Math.floor(TARGET_PAYLOAD_BYTES / bytesPerItem);
  return Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, estimated));
};

const fetchAllPaginatedResults = async <T extends BaseApiContract>(
  props: ContractRequestProps<T>,
): Promise<T["responseType"]> => {
  const initialPageSize = props.pagination?.pageSize ?? 100;

  // First request to get totalResults and estimate optimal page size
  const firstPage = await apiRequest({
    ...props,
    pagination: { ...props.pagination, page: 1, pageSize: initialPageSize },
  });

  if (
    !firstPage ||
    typeof firstPage !== "object" ||
    !("results" in firstPage)
  ) {
    return firstPage;
  }

  const { results: firstResults, totalResults } = firstPage as PaginatedResults;
  if (totalResults <= firstResults.length) {
    return firstPage;
  }

  const optimalPageSize = estimatePageSize(firstResults, initialPageSize);
  const totalPages = Math.ceil(totalResults / optimalPageSize);

  // Reuse first page results when page size hasn't changed
  const canReuseFirstPage = optimalPageSize === initialPageSize;
  const allResults: unknown[][] = canReuseFirstPage ? [firstResults] : [];
  const startPage = canReuseFirstPage ? 2 : 1;

  const pagesToFetch = Array.from(
    { length: totalPages - startPage + 1 },
    (_, i) => i + startPage,
  );

  for (let i = 0; i < pagesToFetch.length; i += MAX_PARALLEL_REQUESTS) {
    const batch = pagesToFetch.slice(i, i + MAX_PARALLEL_REQUESTS);
    const batchResults = await Promise.all(
      batch.map((page) =>
        apiRequest({
          ...props,
          pagination: { ...props.pagination, page, pageSize: optimalPageSize },
        }),
      ),
    );
    for (const res of batchResults) {
      if (res && typeof res === "object" && "results" in res) {
        allResults.push((res as PaginatedResults).results);
      }
    }
  }

  return {
    ...firstPage,
    results: allResults.flat(),
    page: 1,
    totalResults,
    pageSize: totalResults,
  };
};

/**
 * Sends the request a contract describes and turns failures into thrown
 * `PillarApiError`s. Returns the untouched response, or null for a 204.
 */
const sendContractRequest = async <T extends BaseApiContract>({
  contract,
  inputs,
  filters,
  pagination,
  customHeaders,
}: ContractRequestProps<T>): Promise<Response | null> => {
  const params = inputs?.params ? inputs.params : {};

  //Do function to replace the dynamic values in the apiRoute
  let route = buildUrl(contract.apiRoute, params as Params);

  let response;

  const isPillarAdmin = !process.env.REACT_APP_PILLAR_API_URL;

  if (filters || pagination) {
    route = addFiltersAndPaginationToUrl(route, filters ?? {}, pagination);
  }
  if (contract.method === HttpMethod.GET) {
    response = await fetch(route, {
      credentials: "include",
      ...(isPillarAdmin ? { next: { revalidate: 0 } } : {}),
      cache: "no-store",
      redirect: "follow",
      headers: { ...(isPillarAdmin ? { "pillar-admin": "true" } : {}) },
    });
  } else {
    response = await fetch(route, {
      credentials: "include",
      method: contract.method,
      headers: {
        ...(customHeaders != undefined && customHeaders != null
          ? customHeaders
          : RequestHeader.defaultJSON),
        ...(isPillarAdmin ? { "pillar-admin": "true" } : {}),
      },
      redirect: "follow",
      body: inputs?.body ? JSON.stringify(inputs.body) : undefined,
      ...(isPillarAdmin ? { next: { revalidate: 0 } } : {}),
      cache: "no-store",
    });
  }
  if (response.status === 204) {
    return null;
  }
  if (response.status >= 500 && response.status <= 599) {
    throw pillarApiErrorFromResponse({
      statusCode: response.status,
      body: await readErrorBody({ response }),
      fallbackMessage: "Server Error",
    });
  }

  // 403/401 read the body like every other failure branch does. The server
  // sends `debug` (the scope check that rejected, e.g. "Scope" vs "ProfileId")
  // and, outside prod, the session it decoded - without it the caller only ever
  // sees "Forbidden" and has no way to tell an auth bug from a permission one.
  if (response.status === 403) {
    const error = pillarApiErrorFromResponse({
      statusCode: 403,
      body: await readErrorBody({ response }),
      fallbackMessage: "Forbidden",
    });
    window.location.href = "/forbidden?redirectUrl=" + window.location.pathname;
    throw error;
  }
  if (response.status === 401) {
    const error = pillarApiErrorFromResponse({
      statusCode: 401,
      body: await readErrorBody({ response }),
      fallbackMessage: "Unauthorized",
    });
    window.location.href = "/login?redirectUrl=" + window.location.pathname;
    throw error;
  }

  if (response.status >= 400 && response.status <= 499) {
    const errorBody = await readErrorBody({ response });
    // An error status whose envelope still says isSuccessful:true is a
    // deliberate answer rather than a failure - a 409 saying "that user name is
    // taken", say - so its body falls through to the caller instead of being
    // thrown.  readErrorBody reads a clone, so the body is still unread here.
    if (errorBody?.isSuccessful !== true) {
      throw pillarApiErrorFromResponse({
        statusCode: response.status,
        body: errorBody,
        fallbackMessage: response.statusText,
      });
    }
  }

  return response;
};

/**
 * Calls a contract's endpoint and returns the whole `ApiResponse` envelope the
 * server sends (`body`, `status`, `isSuccessful`, ...), or null for a 204.
 * Use `apiRequest` unless you need more than the body.
 */
export const apiRequestRaw = async <T extends BaseApiContract>(
  props: ApiRequestRawProps<T>,
): Promise<ApiResponse<T["responseType"]> | null> => {
  const response = await sendContractRequest(props);
  if (!response) {
    return null;
  }
  return (await response.json()) as ApiResponse<T["responseType"]>;
};

/**
 * Calls a contract's endpoint and returns the response body, typed by the
 * contract. A 204 gives undefined.
 */
export const apiRequest = async <T extends BaseApiContract>({
  flattenPagination = false,
  ...props
}: ApiRequestProps<T>): Promise<T["responseType"]> => {
  if (flattenPagination) {
    return await fetchAllPaginatedResults(props);
  }
  const apiResponse = await apiRequestRaw(props);
  return apiResponse?.body;
};

/**
 * Calls a contract's endpoint and hands the successful `Response` to `parser`,
 * for responses that aren't an `ApiResponse` JSON envelope (file downloads,
 * redirects). Returns what `parser` returns, or null for a 204.
 */
export const apiRequestCustom = async <T extends BaseApiContract, ParsedType>({
  parser,
  ...props
}: ApiRequestCustomProps<T, ParsedType>): Promise<ParsedType | null> => {
  const response = await sendContractRequest(props);
  if (!response) {
    return null;
  }
  return await parser(response);
};

export const replaceTemplate = (
  template: string,
  params: Params,
): { result: string; usedKeys: Set<string> } => {
  const usedKeys = new Set<string>();

  // [[...key]]
  let result = template.replace(/\[\[\.\.\.([^\]]+)\]\]/g, (match, p1) => {
    usedKeys.add(p1);
    const values = params[p1];
    if (values && Array.isArray(values) && values.length > 0) {
      return values.map((value) => String(value)).join("/");
    } else if (values) {
      return String(values);
    } else {
      return "";
    }
  });

  // [...key]
  result = result.replace(/\[\.\.\.([^\]]+)\]/g, (match, p1) => {
    usedKeys.add(p1);
    const values = params[p1];
    if (values && Array.isArray(values) && values.length > 0) {
      return values.map((value) => String(value)).join("/");
    } else if (values) {
      return String(values);
    } else {
      return match;
    }
  });

  // [[key]]
  result = result.replace(/\[\[([^\]]+)\]\]/g, (match, p1) => {
    usedKeys.add(p1);
    return params[p1] !== undefined ? String(params[p1]) : "";
  });

  // [key]
  result = result.replace(/\[([^\]]+)\]/g, (match, p1) => {
    usedKeys.add(p1);
    return params[p1] !== undefined ? String(params[p1]) : match;
  });

  return { result, usedKeys };
};

const buildQueryParams = (params: Params, usedKeys: Set<string>): string => {
  const queryParams = Object.keys(params)
    .filter((key) => !usedKeys.has(key))
    .map((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        return value
          .map(
            (v) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`,
          )
          .join("&");
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(
          String(value),
        )}`;
      }
    })
    .join("&");

  return queryParams ? `?${queryParams}` : "";
};

export const buildUrl = (template: string, params: Params): string => {
  const baseUrl = process.env.REACT_APP_PILLAR_API_URL || "";
  const { result, usedKeys } = replaceTemplate(template, params);
  const queryParams = buildQueryParams(params, usedKeys);

  return `${baseUrl}${result}${queryParams}`;
};
